import { Injectable, inject } from '@angular/core';
import { WebSocketService } from '../../../services/web-socket.service';
import { BehaviorSubject, combineLatest, from, merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import {
  CustomNotificationChanel,
  IBonusesWS,
  IFreespinsWS,
  IGameLimitWS,
  ILootboxesWS,
  IPaymentsWS,
  ISockets,
  ITournamentsStartWS,
  ITournamentsStatusItemWS,
  IUserGroupItemWS,
} from './notification-center.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  SsWebSocketsPrivateChanel,
  SsWebSocketsPublicChanel,
} from '../../../vendor/ss-web-sockets/ss-web-sockets.types';
import { ScrollService } from '../../../services/scroll.service';
import { BonusStage } from '../../../services/user/data/user-bonuses.data';
import { TranslationService } from '../../../services/translation/translation.service';
import { GamesService } from '../../../services/games/games.service';
import { FiltersService } from '../../../services/filters.service';
import { CommonDataService } from '../../../services/common-data.service';
import { TournamentsService } from '../../../../page/tournaments/tournaments.service';
import { LanguageService } from '../../../services/language.service';
import { LootboxService } from '../../../services/lootbox/lootbox.service';
import { DecimalPipe } from '@angular/common';
import { LocalstorageService } from '../../../services/localstorage.service';
import { ArrayService } from '../../../services/array.service';
import { getRandomNumber, isArray } from '../../../helpers/utils';
import { PlatformService } from 'src/app/core/services/platform.service';
import { InstallAppService } from '../../../services/install-app.service';
import { UserGroupsService } from '../../../services/user/user-groups.service';
import { CookieService } from 'ngx-unificator/services';
import { UserService } from '../../../services/user/user.service';

export enum GameLimitsReason {
  FORBIDDEN_WITH_BONUS = 'game_forbidden_with_bonus',
  BONUS_BET_LIMIT_EXCEEDED = 'bonus_bet_limit_exceeded',
  BET_LIMIT_EXCEEDED = 'bet_limit_exceeded',
  SESSION_LIMIT_EXCEEDED = 'session_limit_exceeded',
}

export const RELOAD_BONUSES_TIME_TO_SHOW = {
  TUESDAY_BONUS: {
    allowed_always: false,
    allowed_days: [2],
    title: 't.tuesday-bonus-title',
    description: 't.tuesday-bonus-description',
    prize: 't.tuesday-bonus-prize',
    secondPrize: 't.tuesday-bonus-second-prize',
    image: '/assets/img/bonuses/tuesday-bonus.png',
    active: false,
    bonusCode: 'TU60',
  },
  TOP_SLOTS: {
    allowed_days: null,
    allowed_always: true,
    title: 't.extra-1-fiat-title',
    description: 't.extra-1-fiat-desc',
    prize: 't.top-slots-prize',
    secondPrize: 't.top-slots-second-prize',
    image: '/assets/img/bonuses/fiat/extra-1.png',
    active: false,
    bonusCode: 'TOP40',
  },
  WEEKEND_RELOAD: {
    allowed_always: false,
    allowed_days: [0, 6],
    title: 't.daily-fiat-title',
    description: 't.daily-fiat-desc',
    prize: 't.weekend-reload-prize',
    secondPrize: 't.weekend-reload-second-prize',
    image: '/assets/img/bonuses/crypto/daily.png',
    active: false,
    bonusCode: 'WEEKEND',
  },
};


export const CUSTOM_LOOTBOX_BY_TITLE = {
  lootbox_spinder: {
    link: '/promo/valentine',
    key: 't.notify-spinder-lootbox-desc',
  },
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  private _ws = inject(WebSocketService);
  private _scroll = inject(ScrollService);
  private _translate = inject(TranslationService);
  private _games = inject(GamesService);
  private _filters = inject(FiltersService);
  private _commonData = inject(CommonDataService);
  private _tournaments = inject(TournamentsService);
  private _lang = inject(LanguageService);
  private _lootbox = inject(LootboxService);
  private _data = inject(CommonDataService);
  private _number = inject(DecimalPipe);
  private _localStorage = inject(LocalstorageService);
  private _array = inject(ArrayService);
  private _platform = inject(PlatformService);
  private _installApp = inject(InstallAppService);
  private _groups = inject(UserGroupsService);
  private _cookie = inject(CookieService);
  private _user = inject(UserService);

  /**
   * Is sidenav menu opened
   */
  public sideNavOpen: boolean;

  public isNotUnique: boolean;
  public isOpened = false;

  /**
   * Notifications subject
   */
  public notifications$: BehaviorSubject<ISockets[]> = new BehaviorSubject<any>([]);

  /**
   * Use for unused bonuses
   * @private
   */
  private _unusedBonuses$: Subject<any> = new Subject<any>();

  /**
   * Use for install app custom chanel
   * @private
   */
  private _installAppChannel$: Subject<any> = new Subject<any>();

  private _vipChannel$: Subject<any> = new Subject<any>();

  private _welcomeBonusesChannel$: Subject<any> = new Subject<any>();
  private _reloadBonusesChannel$: Subject<any> = new Subject<any>();

  private _resolvedWelcomeBonusesChannel$: Observable<any> =
    this._welcomeBonusesChannel$.pipe(
      filter(() => !this._groups.isExistGroup('ID407')),
      filter(() => !this._cookie.check(`welcome-bonus-${this._user.stats.deposits_count}`)),
      map((bonus) => ({
        ...bonus,
        notify_channel: CustomNotificationChanel.WELCOME_BONUSES,
        uid: getRandomNumber(111, 111111111111111),
      })),
    );

  private _resolvedReloadBonusesChannel$: Observable<any> =
    this._reloadBonusesChannel$.pipe(
      filter(() => !this._cookie.check('reload-bonus')),
      map(notifications => notifications.map(n => ({
        ...n,
        notify_channel: CustomNotificationChanel.RELOAD_BONUSES,
        uid: getRandomNumber(111, 111111111111111),
      }))),
    );

  /**
   * Resolve fields for unused bonuses message
   * @private
   */
  private _resolvedUnusedBonuses$: Observable<any> = this._unusedBonuses$.pipe(
    map(notifications => notifications.map(n => ({
      ...n,
      notify_channel: CustomNotificationChanel.UNUSED_BONUSES,
      uid: getRandomNumber(111, 111111111111111),
    }))),
  );

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolvedInstallAppChannel$: Observable<any> = this._installAppChannel$.pipe(
    map(() => ({
      notify_channel: CustomNotificationChanel.INSTALL_APP,
      uid: getRandomNumber(111, 111111111111111),
    })),
  );

  private _resolvedVipChannel$: Observable<any> = this._vipChannel$.pipe(
    map(() => ({
      notify_channel: CustomNotificationChanel.VIP,
      uid: getRandomNumber(111, 111111111111111),
    })),
  );

  /**
   * Groups that have already been shown. Use for remove duplicate messages
   * @private
   */
  private _groupsAlreadyBeenShown: any[] = [];

  private _bonuses$: Observable<IBonusesWS> = this._ws.bonuses$;
  private _freespins$: Observable<IFreespinsWS> = this._ws.freespins$;
  private _tournaments$: Observable<ITournamentsStatusItemWS> = this._ws.tournaments$;
  private _tournamentsStarted$: Observable<ITournamentsStartWS> = this._ws.tournamentsStarted$;
  private _lootboxes$: Observable<ILootboxesWS> = this._ws.lootboxes$;
  private _payments$: Observable<IPaymentsWS> = this._ws.payments$;
  private _gameLimits$: Observable<IGameLimitWS> = this._ws.limits$;

  private _groups$: Observable<IUserGroupItemWS | any> = this._ws.groups$.pipe(
    switchMap((groups) => combineLatest([
      of(groups),
      of(groups),
    ])),
    map(([groups, originalGroups]: [any, any]) => [
      Object.entries(groups)
        .filter(([key, value]) => !isNaN(Number(key)))
        .map((g: any[]) => ({ ...g[1], notify_channel: groups?.notify_channel }))
        .filter((g: any) => g.status && !this._groups.isExistGroup(g.id) &&
          !this._groupsAlreadyBeenShown.find(group => g?.id === group?.id)),
      originalGroups,
    ]),
    switchMap(([groups, originalGroups]: [any, any]) => {
      this._groupsAlreadyBeenShown.push(...groups);
      return from(groups);
    }),
  );

  constructor() {
  }

  /**
   * Access for channels
   */
  get channels() {
    return {
      winners: SsWebSocketsPublicChanel.LATEST_WINNERS,
      bonuses: SsWebSocketsPrivateChanel.BONUSES_CHANGED,
      freespins: SsWebSocketsPrivateChanel.FREESPINS_CHANGED,
      compPoints: SsWebSocketsPrivateChanel.COMP_POINTS,
      groups: SsWebSocketsPrivateChanel.GROUPS_UPDATES,
      payments: SsWebSocketsPrivateChanel.PAYMENTS_CHANGED,
      lootboxes: SsWebSocketsPrivateChanel.LOOTBOXES_CHANGED,
      tournaments: SsWebSocketsPrivateChanel.TOURNAMENTS_STATUSES,
      tournamentsStarted: SsWebSocketsPrivateChanel.TOURNAMENTS_STARTED,
      limits: SsWebSocketsPrivateChanel.GAME_LIMITS,
      analytics: SsWebSocketsPrivateChanel.ANALYTICS,
    };
  }

  get customChannels() {
    return CustomNotificationChanel;
  }

  get unusedBonuses$() {
    return this._unusedBonuses$;
  }

  get installAppChannel$() {
    return this._installAppChannel$;
  }

  get vipChannel$() {
    return this._vipChannel$;
  }

  get welcomeBonusesChannel$() {
    return this._welcomeBonusesChannel$;
  }

  get reloadBonusesChannel$() {
    return this._reloadBonusesChannel$;
  }

  /**
   * Init notifications
   */
  public initNotify() {
    if (this._platform.isBrowser) {
      this._localStorage.clearItem('notifications');
      merge(
        this._freespins$,
        this._bonuses$,
        this._lootboxes$,
        this._tournamentsStarted$,
        this._payments$,
        this._gameLimits$,
        this._groups$,
        this._tournaments$,
        this._resolvedUnusedBonuses$,
        this._resolvedInstallAppChannel$,
        this._resolvedVipChannel$,
        this._resolvedWelcomeBonusesChannel$,
        this._resolvedReloadBonusesChannel$,
      ).pipe(
        untilDestroyed(this),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        tap((notification: ISockets) => isArray(notification) ?
          notification.forEach(n => this._mapWSNotify(n)) :
          this._mapWSNotify(notification)),
      ).subscribe();
    }
  }

  /**
   * Open sidenav menu
   */
  public open() {
    if (!this.sideNavOpen) {
      this.sideNavOpen = true;
      this._markNotifyRead();
      this.isOpened = true;
      this._scroll.blockScroll();
    }
  }

  /**
   * Close sidenav menu
   */
  public close() {
    if (this.sideNavOpen) {
      this.sideNavOpen = false;
      this._scroll.UnblockScroll();
      this._markNotifyRead();
    }
  }

  /**
   * Toggle sidenav menu
   */
  public toggle() {
    this.sideNavOpen ? this.close() : this.open();
  }

  /**
   * Remove notifications
   * @param notify
   */
  public removeNotify(notify) {
    const filtered = this.notifications$.getValue().filter(n => n.uid !== notify.uid);
    if (!filtered.length) {
      this.toggle();
      this.notifications$.next([]);
    } else {
      this.notifications$.next(filtered);
    }

    if (notify?.notify_channel === CustomNotificationChanel.INSTALL_APP) {
      this._installApp.closeNotificationBlock();
    }

    if (notify?.notify_channel === CustomNotificationChanel.RELOAD_BONUSES) {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      this._cookie.set('reload-bonus', '1', endOfDay, '/', (window.location.hostname as any));
    }

    if (notify?.notify_channel === CustomNotificationChanel.WELCOME_BONUSES) {
      this._cookie.set(`welcome-bonus-${this._user.stats.deposits_count}`, '1', 999, '/', (window.location.hostname as any));
    }
  }

  /**
   * Map types of notifications
   * @param notification
   * @private
   */
  private _mapWSNotify(notification: ISockets | any) {
    this._checkUniqueNotify();
    switch (true) {
      case notification.notify_channel === CustomNotificationChanel.RELOAD_BONUSES:
        this._resolveReloadChannel(notification);
        break;
      case notification.notify_channel === CustomNotificationChanel.WELCOME_BONUSES:
        this._resolveWelcomeChannel(notification);
        break;
      case notification.notify_channel === CustomNotificationChanel.VIP:
        this._resolveVipChannel(notification);
        break;
      case notification.notify_channel === CustomNotificationChanel.INSTALL_APP:
        this._resolveInstallAppChannel(notification);
        break;
      case notification.notify_channel === CustomNotificationChanel.UNUSED_BONUSES:
        this._resolveUnusedBonus(notification);
        break;
      case notification.stage === BonusStage.ISSUED &&
      notification.notify_channel === SsWebSocketsPrivateChanel.BONUSES_CHANGED:
        this._resolveNotifyBonuses(notification);
        break;
      case notification.stage === BonusStage.ISSUED &&
      notification.notify_channel === SsWebSocketsPrivateChanel.FREESPINS_CHANGED:
        this._resolveNotifyFreespins(notification);
        break;
      case notification.stage === BonusStage.ISSUED &&
      notification.notify_channel === SsWebSocketsPrivateChanel.LOOTBOXES_CHANGED:
        this._resolveNotifyLootboxes(notification);
        break;
      case notification.notify_channel === SsWebSocketsPrivateChanel.PAYMENTS_CHANGED:
        this._resolveNotifyPayments(notification);
        break;
      case notification.notify_channel === SsWebSocketsPrivateChanel.GAME_LIMITS:
        this._resolveGameLimits(notification);
        break;
      case notification.notify_channel === SsWebSocketsPrivateChanel.GROUPS_UPDATES:
        this._resolveGroupUpdates(notification);
        break;
      case notification.notify_channel === SsWebSocketsPrivateChanel.TOURNAMENTS_STATUSES:
        this._resolveTournaments(notification);
        break;
      case notification.notify_channel === SsWebSocketsPrivateChanel.TOURNAMENTS_STARTED:
        this._resolveTournamentsStarted(notification);
        break;
    }
  }

  /**
   * Map game and get game name
   * @param notification
   * @private
   */
  private _mapGameFromSocket(notification: ISockets): Observable<string> {
    return this._games.list({
      'external_id[]': notification.game,
    }).pipe(
      first(),
      filter(response => !!response),
      map(gameResponse => {
        const gamesByExternalId = this._filters.valueAsKeyObject('externalId', gameResponse.gameList || []);
        return gamesByExternalId[notification.game]?.name;
      }),
    );
  }

  /**
   * Map reason of game limit
   * @param notification
   * @param gameName
   * @private
   */
  private _mapReasonOfLimit(notification: ISockets, gameName: string): ISockets {
    return {
      ...notification,
      title: this._translate.translate(this._getTranslateForGameReason(notification.reason), {
        game: gameName,
        limit: notification.limit_cents ?
          this._commonData.subunitsToUnits(notification.limit_cents, notification.currency) : '',
        currency: notification.currency,
      }),
    };
  }

  private _resolveUnusedBonus(notification: any) {
    let bonus = this._getTranslateForBonus(notification.title);
    bonus = bonus.includes('t.') ? this._translate.translate(bonus) : bonus;
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.notify-unused-bonuses-desc', { bonus }),
      },
    ]);
  }

  private _resolveInstallAppChannel(notification: any) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.all-games-bonuses-app'),
        link: '/mobile-app',
      },
    ]);
  }

  private _resolveReloadChannel(notification: any) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate(notification.title),
        description: this._translate.translate(notification.description),
        link: '/deposit',
      },
    ]);
  }

  private _resolveWelcomeChannel(notification: any) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        link: '/deposit',
      },
    ]);
  }

  private _resolveVipChannel(notification: any) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.vip-notification-title'),
        link: '/vip-program',
      },
    ]);
  }

  private _resolveTournamentsStarted(notification: any) {
    this._tournaments.list({ with_game_list: 1 }).pipe(
      filter(list => !!list),
      map(list => [...list.now]),
      map((list) => {
        const currentTournament = list.find(tournament => Number(tournament.identifier) === notification?.id);
        this.notifications$.next([
          ...this.notifications$.getValue(),
          {
            ...notification,
            title: this._translate.translate('t.tournament-started', {
              title: currentTournament?.Title,
            }),
            link: `/${this._lang.current}/tournaments/${currentTournament.slug}`,
          },
        ]);
      }),
    ).subscribe();
  }

  private _resolveTournaments(notification: any) {
    this._tournaments.list({ with_game_list: 1 }).pipe(
      filter(list => !!list),
      map(list => [...list.now, ...list.future, ...list.past]),
      map((list) => {

        [...Object.values(notification)]?.filter((e: any) => e?.tournament_id).forEach((notify: any) => {

          const currentTournament = list.find(tournament => Number(tournament.identifier) === notify?.tournament_id);

          if (currentTournament && Boolean(notify?.award_place)) {
            this.notifications$.next([
              ...this.notifications$.getValue(),
              {
                ...notification,
                notify_channel: notification.notify_channel,
                title: this._translate.translate('t.notify-tournaments-desc', {
                  award_place: notify?.award_place,
                  points: notify?.points,
                  title: currentTournament?.Title,
                }),
                link: `/${this._lang.current}/tournaments/${currentTournament.slug}`,
              },
            ]);
          }
        });
      }),
    ).subscribe();
  }

  /**
   * Find the highest loyalty level and return title with this level
   * @param notification
   * @private
   */
  private _resolveGroupUpdates(notification: ISockets) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.notify-level', {
          name: notification.name,
        }),
      },
    ]);
  }

  /**
   * Resolve games limits
   * @param notification
   * @private
   */
  private _resolveGameLimits(notification: ISockets) {
    this._mapGameFromSocket(notification).subscribe((gameName: string) => {
      this.notifications$.next([
        ...this.notifications$.getValue(),
        this._mapReasonOfLimit(notification, gameName),
      ]);
    });
  }

  /**
   * Resolve bonuses
   * @param notification
   * @private
   */
  private _resolveNotifyBonuses(notification: ISockets) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.notify-bonuses-desc', {
          bonus: this._getTranslateForBonus(notification.title),
        }),
      },
    ]);
  }

  /**
   * Resolve freespins
   * @param notification
   * @private
   */
  private _resolveNotifyFreespins(notification: ISockets) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate('t.notify-freespins-desc', {
          count: notification.freespins_total,
        }),
      },
    ]);
  }

  /**
   * Resolve lootboxes
   * @param notification
   * @private
   */
  private _resolveNotifyLootboxes(notification: ISockets) {
    this._lootbox.lootboxesTranslates$.pipe(
      take(1),
      map((data) => {
        const translate = this._lootbox.resolveLootboxTranslates(notification);
        this.notifications$.next([
          ...this.notifications$.getValue(),
          {
            ...notification,
            title: this._translate.translate(
              CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.key || 't.notify-lootbox-desc',
              CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.description ? null : { bonus: this._translate.translate(translate.title) },
            ),
            link: CUSTOM_LOOTBOX_BY_TITLE[notification.group_key] ? `${CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.link}` :
              `/${this._lang.current}/account`,
          },
        ]);
      }),
    ).subscribe();
  }

  /**
   * Resolve payment changes
   * @param notification
   * @private
   */
  private _resolveNotifyPayments(notification: ISockets) {
    this.notifications$.next([
      ...this.notifications$.getValue(),
      {
        ...notification,
        title: this._translate.translate(notification.success ?
          't.notify-payments-desc-accepted' :
          't.notify-payments-desc-discarded', {
          action: notification.action,
          payment_system: notification.payment_system,
          amount_cents: this._number.transform(
            this._data.subunitsToUnits(notification.amount_cents, notification.currency),
            this._data.isCryptoCurrency(notification.currency) ? '0.8' : '0.2'),
          currency: notification.currency,
        }),
      },
    ]);
  }

  /**
   * Get translate key for game reason
   * @param reason
   * @private
   */
  private _getTranslateForGameReason(reason: GameLimitsReason) {
    return reason === GameLimitsReason.FORBIDDEN_WITH_BONUS ? 't.game-forbidden-with-bonus' :
      reason === GameLimitsReason.BONUS_BET_LIMIT_EXCEEDED ? 't.bonus-bet-limit' :
        reason === GameLimitsReason.BET_LIMIT_EXCEEDED ? 't.bet-limit' :
          reason === GameLimitsReason.SESSION_LIMIT_EXCEEDED ? 't.session-limit' : '';
  }

  /**
   * Get translate key for bonus
   * @param bonusTitle
   * @private
   */
  private _getTranslateForBonus(bonusTitle: string) {
    return bonusTitle.replace(/\[\S*]\s/gim, '');
  }

  private _markNotifyRead() {
    this._checkUniqueNotify();
    this._localStorage.set('notifications', JSON.stringify(this.notifications$.getValue()));
  }

  private _checkUniqueNotify() {
    const notifyArr: any = JSON.parse(this._localStorage.get('notifications'));
    if (!notifyArr) {
      return;
    }
    const notifyArrNow = this.notifications$.getValue();

    const result = this._array.arrayDifference(
      notifyArr,
      notifyArrNow,
      (o1, o2) => o1.id === o2.id,
    );

    if (result.length > 0) {
      this.isNotUnique = false;
      this.isOpened = false;
    } else {
      this.isNotUnique = true;
    }
  }

  public getActiveReloadBonusNotification(date = new Date()) {
    if (this._groups.isExistGroup('ID407')) {
      const day = date.getUTCDay();

      return Object.entries(RELOAD_BONUSES_TIME_TO_SHOW)
        .filter(([_, bonus]) =>
          bonus.allowed_always || bonus.allowed_days.includes(day))
        .map(([_, { title, description, image, bonusCode, prize, secondPrize, active }]) => ({
          title,
          description,
          image,
          bonusCode,
          prize,
          secondPrize,
          active,
        }));
    } else {
      return [];
    }
  }

}
